import { useCallback, useEffect, useRef } from 'react'

import { DropzoneVisualState } from './dropzone.types'

interface UseWindowDropOptions {
  enabled: boolean
  disabled: boolean
  atCapacity: boolean
  onDrop: (files: FileList) => void
  onDragStateChange: (state: DropzoneVisualState) => void
}

export const useWindowDrop = ({
  enabled,
  disabled,
  atCapacity,
  onDrop,
  onDragStateChange,
}: UseWindowDropOptions): void => {
  const counterRef = useRef(0)

  const reset = useCallback(() => {
    counterRef.current = 0
    onDragStateChange('none')
  }, [onDragStateChange])

  useEffect(() => {
    if (!enabled || disabled) return

    const onEnter = (e: DragEvent): void => {
      e.preventDefault()
      counterRef.current += 1

      if (e.dataTransfer?.types.includes('Files')) {
        onDragStateChange('window')
      }
    }

    const onLeave = (e: DragEvent): void => {
      e.preventDefault()
      counterRef.current -= 1

      if (counterRef.current <= 0) {
        reset()
      }
    }

    const onOver = (e: DragEvent): void => {
      e.preventDefault()

      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = atCapacity ? 'none' : 'copy'
      }
    }

    const onWindowDrop = (e: DragEvent): void => {
      e.preventDefault()
      reset()

      if (e.dataTransfer?.files.length) {
        onDrop(e.dataTransfer.files)
      }
    }

    window.addEventListener('dragenter', onEnter)
    window.addEventListener('dragleave', onLeave)
    window.addEventListener('dragover', onOver)
    window.addEventListener('drop', onWindowDrop)
    window.addEventListener('blur', reset)
    document.addEventListener('visibilitychange', reset)

    return () => {
      window.removeEventListener('dragenter', onEnter)
      window.removeEventListener('dragleave', onLeave)
      window.removeEventListener('dragover', onOver)
      window.removeEventListener('drop', onWindowDrop)
      window.removeEventListener('blur', reset)
      document.removeEventListener('visibilitychange', reset)
    }
  }, [enabled, disabled, atCapacity, onDrop, onDragStateChange, reset])
}

interface UseDropzoneHandlersOptions {
  disabled: boolean
  atCapacity: boolean
  onDrop: (files: FileList) => void
  onDragStateChange: (state: DropzoneVisualState) => void
}

interface DropzoneHandlers {
  handleDragEnter: (e: React.DragEvent) => void
  handleDragLeave: (e: React.DragEvent) => void
  handleDragOver: (e: React.DragEvent) => void
  handleDrop: (e: React.DragEvent) => void
}

export const useDropzoneHandlers = ({
  disabled,
  atCapacity,
  onDrop,
  onDragStateChange,
}: UseDropzoneHandlersOptions): DropzoneHandlers => {
  const counterRef = useRef(0)

  const handleDragEnter = useCallback(
    (e: React.DragEvent): void => {
      e.preventDefault()
      e.stopPropagation()

      if (disabled) return

      counterRef.current += 1

      if (e.dataTransfer.types.includes('Files')) {
        onDragStateChange('over')
      }
    },
    [disabled, onDragStateChange],
  )

  const handleDragLeave = useCallback(
    (e: React.DragEvent): void => {
      e.preventDefault()
      e.stopPropagation()

      counterRef.current -= 1

      if (counterRef.current <= 0) {
        counterRef.current = 0
        onDragStateChange('none')
      }
    },
    [onDragStateChange],
  )

  const handleDragOver = useCallback(
    (e: React.DragEvent): void => {
      e.preventDefault()
      e.stopPropagation()

      if (!disabled) {
        e.dataTransfer.dropEffect = atCapacity ? 'none' : 'copy'
      }
    },
    [disabled, atCapacity],
  )

  const handleDrop = useCallback(
    (e: React.DragEvent): void => {
      e.preventDefault()
      e.stopPropagation()

      counterRef.current = 0
      onDragStateChange('none')

      if (disabled) return

      const { files } = e.dataTransfer

      if (files.length > 0) {
        onDrop(files)
      }
    },
    [disabled, onDrop, onDragStateChange],
  )

  return { handleDragEnter, handleDragLeave, handleDragOver, handleDrop }
}
